import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMemo, useState, type ReactElement } from 'react';
import { getSessionAgent } from '../utils/session';

type IconName =
  | 'dashboard'
  | 'appointments'
  | 'bookings'
  | 'messages'
  | 'payments'
  | 'support'
  | 'help'
  | 'settings'
  | 'agents';

type NavigationItem = {
  label: string;
  path: string;
  iconName: IconName;
  badge?: string;
};

const baseNavItems: NavigationItem[] = [
  { label: 'Dashboard', path: '/dashboard', iconName: 'dashboard' },
  // { label: 'Appointments', path: '/appointments', iconName: 'appointments' },
  // { label: 'My bookings', path: '/bookings', iconName: 'bookings' },
  { label: 'Messages', path: '/messages', iconName: 'messages' },

  // { label: 'Payments', path: '/payments', iconName: 'payments' },
  // { label: 'Support', path: '/support', iconName: 'support' },
  // { label: 'Help center', path: '/help-center', iconName: 'help' },
  // { label: 'Settings', path: '/settings', iconName: 'settings' },
  { label: 'Role Management', path: '/role-management', iconName: 'settings' },
];

const navIconMap: Record<IconName, ReactElement> = {
  dashboard: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 13h6V5H4zM14 21h6v-8h-6zM14 5h6v6h-6zM4 21h6v-6H4z" />
    </svg>
  ),
  appointments: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M16 3v4M8 3v4M3 11h18" />
    </svg>
  ),
  bookings: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 6h16M5 10h14M7 14h10M10 18h4" />
      <path d="M3 3h18v18H3z" />
    </svg>
  ),
  messages: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 4h14a2 2 0 0 1 2 2v11l-3-3H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      <path d="M17 17l-2 2-2-2" />
    </svg>
  ),
  payments: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="7" width="18" height="12" rx="2" />
      <path d="M5 11h14M5 15h5" />
    </svg>
  ),
  support: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 1a9 9 0 0 1 9 9v4a5 5 0 0 1-5 5h-1l-2 3-2-3H8a5 5 0 0 1-5-5V10a9 9 0 0 1 9-9z" />
      <circle cx="12" cy="16" r="1" />
    </svg>
  ),
  help: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M9.09 9a2.91 2.91 0 0 1 5.82 0c0 2.06-2 2.5-2 2.5v1" />
      <path d="M12 17h.01" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 1 .33 1.82l-1.2 2a2 2 0 0 1-2.6.7l-1.7-.9a2 2 0 0 0-1.86 0l-1.7.9a2 2 0 0 1-2.6-.7l-1.2-2a1.65 1.65 0 0 1 .33-1.82l1.4-1.4a1.65 1.65 0 0 0 0-2.34l-1.4-1.4A1.65 1.65 0 0 1 5.5 6l1.2-2a2 2 0 0 1 2.6-.7l1.7.9a2 2 0 0 0 1.86 0l1.7-.9a2 2 0 0 1 2.6.7l1.2 2a1.65 1.65 0 0 1-.33 1.82l-1.4 1.4a1.65 1.65 0 0 0 0 2.34z" />
    </svg>
  ),
  agents: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="9" cy="8" r="3" />
      <path d="M18 8h.01" />
      <path d="M3 21v-2a4 4 0 0 1 4-4h3a4 4 0 0 1 4 4v2" />
      <path d="M14 16h6" />
    </svg>
  ),
};

const headerActions = [
  {
    label: 'Calendar',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="3" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    ),
  },
  {
    label: 'Chat',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 5h16a2 2 0 0 1 2 2v10l-4-4H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" />
      </svg>
    ),
  },
  {
    label: 'Notifications',
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },
];

function AdminLayout() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = useMemo(() => Boolean(getSessionAgent()?.isAdmin), []);

  const navItems = useMemo<NavigationItem[]>(() => {
    const items = [...baseNavItems];
    if (isAdmin) {
      items.splice(3, 0, { label: 'Agents list', path: '/agents', iconName: 'agents' });
    }
    return items;
  }, [isAdmin]);

  const sidebarWidthClass = isCollapsed ? 'md:ml-[92px]' : 'md:ml-[280px]';
  const pageTitleMap: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/messages': 'Messages',
    '/role-management': 'Role management',
    '/agents': 'Agents list',
    '/appointments': 'Appointments',
    '/bookings': 'Bookings',
    '/payments': 'Payments',
    '/support': 'Support',
    '/help-center': 'Help center',
    '/settings': 'Settings',
  };
  const pageTitle = pageTitleMap[location.pathname] ?? 'Dashboard';
  const isDashboard = location.pathname === '/dashboard';

  const handleLogout = () => {
    setProfileOpen(false);
    localStorage.clear();
    navigate('/');
  };

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-emerald-50 to-white">
      <div className="flex h-screen relative">
        <div
          className={`fixed inset-0 z-20 bg-slate-900/40 transition-opacity duration-300 md:hidden ${isMobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
            }`}
          onClick={() => setIsMobileOpen(false)}
          aria-hidden
        />
        <aside
          style={{ width: isCollapsed ? 96 : 280 }}
          className={`fixed inset-y-0 left-0 z-30 flex flex-col border border-emerald-100 bg-white shadow-lg shadow-slate-200/70 transition-transform duration-300 md:translate-x-0 ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
        >
          <div className={`flex flex-col ${isCollapsed ? 'space-y-6 px-3 py-6' : 'space-y-8 px-6 py-8'} relative min-h-full`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src="/headerlogo.jpg"
                  alt="Carewinners Logo"
                  className={`rounded-2xl border border-emerald-200 ${isCollapsed ? 'h-16 w-16' : 'h-20 w-20'}`}
                />
                {!isCollapsed && (
                  <div className="text-sm">
                    <p className="text-xs uppercase tracking-[0.6em] text-emerald-500">Carewinners</p>
                    <p className="font-semibold text-slate-900">BETA</p>
                  </div>
                )}
              </div>
              <button
                type="button"
                title="Toggle sidebar"
                className="mt-10 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 shadow-sm shadow-slate-200 transition hover:scale-105"
                onClick={() => {
                  setIsCollapsed((prev) => !prev);
                  setProfileOpen(false);
                }}
              >
                <svg viewBox="0 0 20 20" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 5h8M6 10h7M6 15h8" />
                </svg>
              </button>
            </div>

            <nav className="space-y-1 flex-1 text-sm">
              {navItems.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.path}
                  onClick={() => setIsMobileOpen(false)}
                  className={({ isActive }) => {
                    const base =
                      'flex items-center gap-3 rounded-2xl p-3 transition text-slate-800 hover:bg-[#78C45E]/20 hover:shadow-sm';
                    const active =
                      'bg-[#78C45E] text-white font-semibold shadow-inner shadow-emerald-200';
                    return `${base} ${isCollapsed ? 'justify-center' : 'justify-start'} ${isActive ? active : ''
                      }`;
                  }}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-50 border border-slate-200">
                    {navIconMap[item.iconName]}
                  </span>
                  {!isCollapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              ))}
            </nav>

            <div className={`absolute bottom-0 left-0 right-0 ${isCollapsed ? 'px-3 pb-6' : 'px-6 pb-8'}`}>
              <button
                type="button"
                className={`flex w-full items-center gap-3 rounded-2xl bg-[#78C45E] px-3 py-3 text-sm font-semibold text-white transition hover:bg-[#78C45E]/90 ${isCollapsed ? 'justify-center px-0' : 'justify-start'
                  }`}
                onClick={handleLogout}
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 17l5-5-5-5M21 12H9M9 5v14" />
                </svg>
                {!isCollapsed && <span>Logout</span>}
              </button>
            </div>
          </div>
        </aside>

        <main
          className={`relative flex-1 px-6 py-8 transition-all duration-300 ${sidebarWidthClass}`}
        >
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-2xl border border-emerald-200 bg-white shadow-sm shadow-slate-200 md:hidden"
                onClick={() => setIsMobileOpen(true)}
              >
                <svg viewBox="0 0 20 20" className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h14M3 10h14M3 14h14" />
                </svg>
              </button>
              <div>
                <p className="text-xs uppercase tracking-[0.6em] text-emerald-500">Carewinners admin</p>
                <p className="text-lg font-semibold text-slate-900">{pageTitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {headerActions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  className="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-100 bg-white text-slate-600 shadow-sm shadow-slate-200 transition hover:border-emerald-200"
                  title={action.label}
                >
                  {action.icon}
                </button>
              ))}
              <div className="relative">
                <button
                  type="button"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-emerald-600 shadow-sm shadow-slate-200"
                  onClick={() => setProfileOpen((prev) => !prev)}
                >
                  CW
                </button>
                {profileOpen && (
                  <div className="absolute right-0 top-14 w-56 rounded-2xl border border-emerald-100 bg-white p-4 text-sm shadow-lg shadow-slate-200">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Profile</p>
                    <p className="text-base font-semibold text-slate-900">Carewinners Admin</p>
                    <p className="text-xs text-slate-500">admin@carewinners.org</p>
                    <button
                      type="button"
                      className="mt-3 w-full rounded-full bg-[#78C45E] px-3 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white hover:bg-[#78C45E]/90"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {isDashboard && (
            <div className="mb-6 flex flex-col gap-4 rounded-3xl border border-emerald-100 bg-white/80 px-4 py-3 text-slate-700 shadow-sm shadow-slate-200">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.4em] text-emerald-500">Overview</p>
                  <h3 className="text-lg font-semibold text-slate-900">Dashboard</h3>
                </div>
                <button
                  type="button"
                  className="self-end rounded-2xl border border-emerald-300 px-5 py-2 text-sm font-semibold text-emerald-700 hover:border-emerald-500"
                >
                  Export report
                </button>
              </div>
              <div className="flex items-center justify-between gap-3 text-sm font-semibold text-emerald-600 md:text-base">
                <p>Everything you need to keep the team safe and schedules flowing.</p>
                <div className="flex gap-3">
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs">Today</span>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">Insights</span>
                </div>
              </div>
            </div>
          )}

          <div className="h-full rounded-3xl border border-emerald-100 bg-white/70 p-6 shadow-2xl shadow-slate-900/10 overflow-hidden">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
